import { useLayoutEditorContext } from 'features/views/LayoutEditor/useLayoutEditorContext'
import {
    RelatedRecordWidgetFieldRef,
    RelatedRecordWidgetType,
} from 'features/views/LayoutEditor/widgets/RelatedRecordWidget/types'
import { useRecordManagerContext } from 'features/views/RecordManager/useRecordManagerContext'

import useDeepEqualsMemoValue from 'v2/ui/utils/useDeepEqualsMemoValue'

import { useContainerResponsiveValue } from 'ui/styling/helpers/useResponsiveValue'

import { useRelatedRecordWidgetTarget } from './useRelatedRecordWidgetTarget'

type UseRelatedRecordWidgetStateProps = {
    widget: RelatedRecordWidgetType
}

export function useRelatedRecordWidgetState({ widget }: UseRelatedRecordWidgetStateProps) {
    const { record: targetRecord, isLoadingSlow, isError } = useRecordManagerContext()
    const { isEditing } = useLayoutEditorContext()

    const hasField = !!widget.attrs.targetField

    const { targetObject, targetObjectFields, visibleFields } = useRelatedRecordWidgetTarget(widget)

    const hideWidget =
        (!targetRecord && !isLoadingSlow) ||
        (!hasField && !isEditing) ||
        (!targetRecord && !isLoadingSlow && !isEditing && !isError)

    const fieldsByApiName = new Map(targetObjectFields.map((field) => [field.api_name, field]))

    const titleField = resolveFieldRef(widget.attrs.title, fieldsByApiName)
    const titleValue = targetRecord?.[titleField?.api_name!] ?? targetRecord?._primary ?? undefined

    const profileImageField = resolveFieldRef(widget.attrs.profileImage, fieldsByApiName)
    const profileImageValue = targetRecord?.[profileImageField?.api_name!] ?? undefined

    const subtitleField = resolveFieldRef(widget.attrs.subtitle, fieldsByApiName)
    const subtitleValue = targetRecord?.[subtitleField?.api_name!] ?? undefined

    const descriptionField = resolveFieldRef(widget.attrs.description, fieldsByApiName)
    const descriptionValue = targetRecord?.[descriptionField?.api_name!] ?? undefined

    const labelValue = widget.attrs.label

    const alignment = widget.attrs.alignment ?? 'left'

    const showFieldIcon = widget.attrs.showFieldIcon ?? true

    const labelPlacement = widget.attrs.labelPlacement ?? 'top'

    const { ref, value: isSmall } = useContainerResponsiveValue<HTMLDivElement, boolean>({
        mobile: true,
        tablet: false,
    })

    return useDeepEqualsMemoValue({
        hasField,
        isLoadingSlow,
        hideWidget,
        targetObject,
        isError,
        targetRecord,
        titleField,
        titleValue,
        profileImageField,
        profileImageValue,
        subtitleField,
        subtitleValue,
        descriptionField,
        descriptionValue,
        visibleFields,
        alignment,
        labelValue,
        showFieldIcon,
        ref,
        isSmall,
        labelPlacement,
    })
}

function resolveFieldRef(
    fieldRef?: RelatedRecordWidgetFieldRef,
    fields?: Map<string, FieldDto>
): FieldDto | undefined {
    if (fieldRef?.type === 'field') {
        return fields?.get(fieldRef.fieldApiName)
    }

    return undefined
}
